import { isEmpty } from 'lodash';
import { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '@shared/components/Button';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBIconV2, MBImage } from '@shared/components/image';
import { getComponentIcon, getComponentPlaceholder } from '@shared/scenes/workflows/utils';
import { grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { SignatureClearButton } from './SignatureClearButton';
import styles from './SignatureView.module.css';

export interface SignatureProps {
  component: API.SignatureComponent;
  signatureUrl?: string;
  onSignatureUpdate(data: string): void;
}

export function SignatureView({ component, onSignatureUpdate, signatureUrl }: SignatureProps) {
  const ref = useRef<SignatureCanvas | null>(null);
  const [currentDataUrl, setCurrentDataUrl] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);

  function onBegin() {
    setIsDrawing(true);
  }

  function onEnd() {
    setIsDrawing(false);
    setCurrentDataUrl(ref.current?.toDataURL() ?? '');
  }

  const clear = () => {
    ref.current?.clear();
    setCurrentDataUrl('');
    onSignatureUpdate('');
  };

  const onSave = () => {
    onSignatureUpdate(ref.current?.toDataURL() ?? '');
    setCurrentDataUrl('');
    ref.current?.clear();
  };

  const isReadOnly = component?.readonly ?? false;
  const isEmptySignature = isEmpty(currentDataUrl) && isEmpty(signatureUrl);
  const hasUrl = !isEmptySignature;
  const showCornerButton = hasUrl && !component?.readonly && !isReadOnly;
  const showPlaceholder = !isDrawing && isEmptySignature;
  const showCanvas = !isReadOnly && isEmpty(signatureUrl);
  const showImage = !isEmpty(signatureUrl);
  const showSaveButton = !isReadOnly && !isEmpty(currentDataUrl);

  return (
    <View className={[styles.signatureView, isReadOnly && styles.readonly]}>
      {showCornerButton && <SignatureClearButton onPress={clear} />}
      {showPlaceholder && (
        <>
          <View center row className={styles.placeholder}>
            <MBIconV2
              size={grid(5)}
              name={getComponentIcon(component) ?? 'signature-ballpoint-pen'}
            />
            <View className={styles.placeholderLabel}>
              <Text size="subtitle">{getComponentPlaceholder(component)}</Text>
              <View className={styles.signatureLine} />
            </View>
          </View>
        </>
      )}
      {showCanvas && (
        <SignatureCanvas
          ref={ref}
          onBegin={onBegin}
          onEnd={onEnd}
          canvasProps={{ className: styles.canvas }}
        />
      )}
      {showImage && <MBImage source={{ uri: signatureUrl }} fill resizeMode="contain" />}
      {showSaveButton && (
        <View padding={1} center>
          <Button variant="primary" size="medium" onClick={onSave} label={i18n('common.save')} />
        </View>
      )}
    </View>
  );
}
